import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ippApi, { RANKINGS, PLAN_STATUS, fmtDMY } from '../utils/ippApi';
import { useAuth } from '../context/AuthContext';

const ACCENT = '#7C3AED';
const HubGrid = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="4.5" height="4.5" rx="1" fill="currentColor"/><rect x="9.5" y="2" width="4.5" height="4.5" rx="1" fill="currentColor"/><rect x="2" y="9.5" width="4.5" height="4.5" rx="1" fill="currentColor"/><rect x="9.5" y="9.5" width="4.5" height="4.5" rx="1" fill="currentColor"/></svg>
);

const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit' };
const lbl = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 5, display: 'block' };
const btn = (bg = ACCENT, fg = '#fff') => ({ padding: '8px 14px', borderRadius: 8, border: bg === '#fff' ? '1px solid var(--border)' : 'none', background: bg, color: fg, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' });

// ── Multi-select chip picker ────────────────────────────────────────────────
function MultiUserSelect({ users, value, onChange, placeholder = 'Select people…', disabled }) {
  const [open, setOpen] = useState(false);
  const selected = users.filter(u => value.includes(u.id));
  const toggle = id => onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  return (
    <div style={{ position: 'relative' }}>
      <div onClick={() => !disabled && setOpen(o => !o)} style={{ ...inp, minHeight: 36, cursor: disabled ? 'default' : 'pointer', display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', opacity: disabled ? 0.7 : 1 }}>
        {selected.length === 0 && <span style={{ color: '#9CA3AF' }}>{placeholder}</span>}
        {selected.map(u => (
          <span key={u.id} style={{ fontSize: 11, fontWeight: 600, background: 'rgba(124,58,237,0.10)', color: ACCENT, padding: '2px 7px', borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {u.full_name}
            {!disabled && <span onClick={e => { e.stopPropagation(); toggle(u.id); }} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>}
          </span>
        ))}
      </div>
      {open && !disabled && (
        <div style={{ position: 'absolute', zIndex: 40, top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto', padding: 6 }}>
          {users.length === 0 && <div style={{ fontSize: 12, color: '#9CA3AF', padding: 8 }}>No people</div>}
          {users.map(u => (
            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <input type="checkbox" checked={value.includes(u.id)} onChange={() => toggle(u.id)} />
              <span>{u.full_name}</span>
            </label>
          ))}
          <div style={{ borderTop: '1px solid #F3F4F6', marginTop: 6, paddingTop: 6, textAlign: 'right' }}>
            <button onClick={() => setOpen(false)} style={{ ...btn('#F3F4F6', '#374151'), padding: '5px 10px' }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Modal does NOT close on backdrop click — only via its buttons / ✕, so in-progress
// edits aren't lost by an accidental outside click.
function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 14, width, maxWidth: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 2 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--muted)', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 22 }}>{children}</div>
      </div>
    </div>
  );
}

// In-app confirmation dialog (never the browser's confirm()).
function ConfirmModal({ title = 'Please confirm', message, confirmLabel = 'Confirm', danger, onConfirm, onClose }) {
  return (
    <Modal title={title} onClose={onClose} width={420}>
      <p style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.55, margin: '0 0 20px' }}>{message}</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
        <button onClick={onConfirm} style={btn(danger ? '#DC2626' : ACCENT)}>{confirmLabel}</button>
      </div>
    </Modal>
  );
}

const StatusPill = ({ status }) => {
  const s = PLAN_STATUS[status] || PLAN_STATUS.draft;
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: s.bg, color: s.color }}>{s.label}</span>;
};

export default function IppPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [plan, setPlan] = useState(null);     // full active plan
  const [loading, setLoading] = useState(true);
  const [profileModal, setProfileModal] = useState(false);
  const [goalModal, setGoalModal] = useState(null); // goal being edited or {} for new
  const [evidenceModal, setEvidenceModal] = useState(null); // goal for evidence
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  // Years from 2035 down to a few years back, so plans for future years can be set up.
  const years = []; for (let y = 2035; y >= now.getFullYear() - 2; y--) years.push(y);
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  const loadUsers = useCallback(() => { ippApi.get('/users').then(setUsers).catch(() => setUsers([])); }, []);
  const loadPlans = useCallback(() => {
    setLoading(true);
    ippApi.get(`/plans?year=${year}`).then(rows => {
      setPlans(rows);
      setLoading(false);
    }).catch(() => { setPlans([]); setLoading(false); });
  }, [year]);

  const loadPlan = useCallback((id) => {
    if (!id) { setPlan(null); return; }
    ippApi.get(`/plans/${id}`).then(setPlan).catch(() => setPlan(null));
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadPlans(); setActivePlanId(null); setPlan(null); }, [loadPlans]);
  useEffect(() => { loadPlan(activePlanId); }, [activePlanId, loadPlan]);

  // ── Live updates (SSE): silently re-fetch when anything we can see changes ──
  const liveRef = React.useRef({});
  liveRef.current = { activePlanId, loadPlan, loadPlans };
  useEffect(() => {
    const token = localStorage.getItem('flowup_token');
    let es;
    try {
      es = new EventSource(`/api/ipp/events?token=${encodeURIComponent(token || '')}`);
      es.addEventListener('ipp:changed', () => {
        const { activePlanId, loadPlan, loadPlans } = liveRef.current;
        // Refresh the list and, if a plan is open, refresh it too — silently.
        loadPlans();
        if (activePlanId) loadPlan(activePlanId);
      });
      es.onerror = () => { /* EventSource auto-reconnects; ignore transient errors */ };
    } catch (e) { /* SSE unavailable; app still works, just without live refresh */ }
    return () => { try { es && es.close(); } catch (e) {} };
  }, []);

  // Ensure my own plan exists for the selected year, then open it.
  const createDraft = async () => {
    setBusy(true);
    try {
      const p = await ippApi.post('/plans', { year });
      await loadPlans();
      setActivePlanId(p.id);
    } catch (e) { showToast(e.error || 'Failed'); }
    setBusy(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <header style={{ height: 56, flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 22px', gap: 12 }}>
        <div style={{ width: 32, height: 32, borderRadius: 9, background: `linear-gradient(135deg,${ACCENT},#9F67FF)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M7 3h7l4 4v14a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="#fff" strokeWidth="1.7" strokeLinejoin="round"/><path d="M13 3v5h5M9 13l1.5 1.5L14 11M9 17h6" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <div style={{ lineHeight: 1.15 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>Performance Plans</div>
          <div style={{ fontSize: 10, color: 'var(--muted)' }}>Individual Performance Plan</div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => navigate('/hub')} title="App Hub — switch applications"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--muted2)', cursor: 'pointer' }}>
          <HubGrid />
        </button>
        <span style={{ fontSize: 12.5, color: 'var(--muted2)', fontWeight: 600 }}>{user?.full_name}</span>
        <button onClick={() => { logout(); navigate('/login'); }} style={{ ...btn('var(--surface2)', 'var(--muted2)'), border: '1px solid var(--border)' }}>Sign out</button>
      </header>

      <main style={{ flex: 1, overflow: 'auto', padding: '24px 26px 60px' }}>
        <div style={{ maxWidth: 'min(97vw, 2100px)', margin: '0 auto' }}>
          {/* Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 22, fontWeight: 850, color: 'var(--text)', margin: 0, letterSpacing: '-0.5px' }}>Performance Plans</h1>
            <div style={{ flex: 1 }} />
            <label style={{ fontSize: 12, color: 'var(--muted2)', fontWeight: 600 }}>Year</label>
            <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ ...inp, width: 'auto' }}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => { window.location.href = '/profile'; }} style={btn('var(--surface2)', 'var(--text)')}>⚙ My Information</button>
            <button onClick={createDraft} disabled={busy} style={btn()}>+ New Draft Performance Plan</button>
          </div>

          {/* Plans list */}
          {!activePlanId && (
            loading ? <div style={{ color: 'var(--muted)', fontSize: 13, padding: 30 }}>Loading…</div> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 16 }}>
                {plans.length === 0 && (
                  <div style={{ gridColumn: '1/-1', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 40, textAlign: 'center', color: 'var(--muted2)' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>No draft plans for {year}</div>
                    <div style={{ fontSize: 13 }}>Click “+ New Draft Performance Plan” to start one.</div>
                  </div>
                )}
                {plans.map(p => (
                  <button key={p.id} onClick={() => setActivePlanId(p.id)}
                    style={{ textAlign: 'left', cursor: 'pointer', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: ACCENT }}>{p.relation === 'owner' ? 'My plan' : p.relation === 'manager' ? 'I manage' : 'Admin'}</span>
                      {p.status === 'approved'
                        ? <span style={{ fontSize: 11, fontWeight: 700, color: '#16A34A', background: 'rgba(22,163,74,0.12)', padding: '3px 10px', borderRadius: 999 }}>✓ Approved</span>
                        : <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted2)', background: 'var(--surface2)', padding: '3px 10px', borderRadius: 999 }}>Not approved</span>}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{p.name || 'Draft'}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--muted2)' }}>{p.employee_name} · {p.year}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {p.goal_count || 0} goal{(p.goal_count || 0) === 1 ? '' : 's'}
                      {p.goal_count ? ` · ${p.approved_goal_count}/${p.goal_count} rows approved` : ''}
                      {p.managers?.length ? ` · ${p.managers.length} manager(s)` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )
          )}

          {/* Active plan detail */}
          {activePlanId && plan && (
            <PlanDetail
              plan={plan} users={users} me={user}
              onBack={() => { setActivePlanId(null); loadPlans(); }}
              reload={() => loadPlan(activePlanId)}
              onEditGoal={g => setGoalModal(g)}
              onEvidence={g => setEvidenceModal(g)}
              showToast={showToast}
            />
          )}
        </div>
      </main>

      {profileModal && null}
      {goalModal && plan && (
        <GoalModal plan={plan} users={users} goal={goalModal.id ? goalModal : null}
          onClose={() => setGoalModal(null)} onSaved={() => { setGoalModal(null); loadPlan(activePlanId); }} showToast={showToast} />
      )}
      {evidenceModal && (
        <EvidenceModal goal={evidenceModal} onClose={() => setEvidenceModal(null)} onSaved={() => loadPlan(activePlanId)} showToast={showToast}
          canEdit={!!(plan?.my_access?.can_edit_plan || (evidenceModal.shared_with || []).some(s => s.id === user?.id))} />
      )}
      {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 2000, boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>{toast}</div>}
    </div>
  );
}

// ── Plan detail (header + goals grid + workflow) ────────────────────────────
function PlanDetail({ plan, users, me, onBack, reload, onEditGoal, onEvidence, showToast }) {
  const acc = plan.my_access || {};
  const businessGoals = plan.goals.filter(g => g.goal_type !== 'improvement');
  const improvementGoals = plan.goals.filter(g => g.goal_type === 'improvement');
  const totalWeight = businessGoals.reduce((s, g) => s + (Number(g.weight) || 0), 0);
  const [savingMgr, setSavingMgr] = useState(false);
  const [managerIds, setManagerIds] = useState(plan.managers.map(m => m.id));
  const [delGoal, setDelGoal] = useState(null);
  const [delPlan, setDelPlan] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(plan.name || '');
  useEffect(() => { setManagerIds(plan.managers.map(m => m.id)); }, [plan.id]); // eslint-disable-line

  // Two-level approval gate: count rows not yet approved.
  const allGoals = plan.goals || [];
  const rowOk = (g) => (g.row_status || (g.approved ? 'approved' : 'submitted_draft')) === 'approved';
  const pendingRows = allGoals.filter(g => !rowOk(g)).length;
  const allApproved = allGoals.length > 0 && pendingRows === 0;
  const [planReopen, setPlanReopen] = useState(false);
  const [planReason, setPlanReason] = useState('');

  const renamePlan = () => { setRenameVal(plan.name || ''); setRenaming(true); };
  const confirmRename = async () => {
    const nm = renameVal.trim();
    if (!nm) { setRenaming(false); return; }
    try { await ippApi.put(`/plans/${plan.id}`, { name: nm }); setRenaming(false); reload(); }
    catch (e) { showToast(e.error || 'Failed'); }
  };

  const confirmDelete = async () => {
    try { await ippApi.delete(`/goals/${delGoal.id}`); setDelGoal(null); reload(); }
    catch (e) { showToast(e.error || 'Failed'); setDelGoal(null); }
  };
  const confirmDeletePlan = async () => {
    try { await ippApi.delete(`/plans/${plan.id}`); setDelPlan(false); onBack(); }
    catch (e) { showToast(e.error || 'Failed'); setDelPlan(false); }
  };

  const saveManagers = async (ids) => {
    setManagerIds(ids); setSavingMgr(true);
    try { await ippApi.put(`/plans/${plan.id}`, { manager_ids: ids }); reload(); } catch (e) { showToast(e.error || 'Failed'); }
    setSavingMgr(false);
  };

  const submit = async () => { try { await ippApi.post(`/plans/${plan.id}/submit`, {}); showToast('Plan submitted for approval'); reload(); } catch (e) { showToast(e.error || 'Failed'); } };
  const approve = async () => { try { await ippApi.post(`/plans/${plan.id}/approve`, {}); showToast('Plan fully approved'); reload(); } catch (e) { showToast(e.error || 'Failed'); } };
  const reopenPlan = async (reason) => { try { await ippApi.post(`/plans/${plan.id}/reopen`, { reason }); showToast('Plan reopened as a new version'); reload(); } catch (e) { showToast(e.error || 'Failed'); } };

  const exportDoc = (fmt) => {
    const token = localStorage.getItem('flowup_token');
    const url = `/api/ipp/plans/${plan.id}/export?format=${fmt}&token=${encodeURIComponent(token || '')}`;
    window.open(url, '_blank');
  };

  const th = { textAlign: 'left', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: '#fff', padding: '10px 10px', background: '#2A2550', position: 'sticky', top: 0 };
  const td = { fontSize: 12.5, color: 'var(--text)', padding: '10px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', lineHeight: 1.5, whiteSpace: 'pre-wrap' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={btn('var(--surface2)', 'var(--text)')}>← Back</button>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 850, color: 'var(--text)' }}>{plan.name || 'Draft'}</div>
            {(acc.is_owner || acc.is_admin) && (
              <button onClick={renamePlan} title="Rename this draft" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 13, padding: 2 }}>✎</button>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted2)' }}>{plan.employee?.full_name} · {plan.year}</div>
        </div>
        <StatusPill status={plan.status} />
        <div style={{ flex: 1 }} />
        <button onClick={() => exportDoc('docx')} style={btn('var(--surface2)', 'var(--text)')}>⬇ Word</button>
        <button onClick={() => exportDoc('pdf')} style={btn('var(--surface2)', 'var(--text)')}>⬇ PDF</button>
        {(acc.is_owner || acc.is_admin) && <button onClick={() => setDelPlan(true)} style={btn('#FEF2F2', '#DC2626')}>🗑 Delete plan</button>}
      </div>

      {/* Header info strip */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
        {[['Staff No', plan.profile?.staff_no], ['Position', plan.profile?.position_title], ['Function', plan.profile?.function_name], ['Grade', plan.profile?.salary_grade],
          ['Date to Position', fmtDMY(plan.profile?.date_to_position)], ['Date to SCT', fmtDMY(plan.profile?.date_to_sct)], ['Join Date', fmtDMY(plan.profile?.join_date)],
          ['Period', `${fmtDMY(plan.period_start)} – ${fmtDMY(plan.period_end)}`]].map(([k, v]) => (
          <div key={k}><div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>{k}</div><div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{v || '—'}</div></div>
        ))}
      </div>

      {/* Managers for the year (plan level) */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <label style={lbl}>Line Manager(s) for {plan.year} {savingMgr && <span style={{ color: ACCENT }}>· saving…</span>}</label>
        <MultiUserSelect users={users} value={managerIds} onChange={saveManagers}
          placeholder="Assign manager(s) who can view, edit & approve this plan"
          disabled={!(acc.is_admin || acc.is_owner)} />
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>Managers can view and edit the whole plan, approve individual rows, and approve the plan.</div>
      </div>

      {/* Goals grid */}
      <GoalsTable title="Business / Individual Goals" goals={businessGoals} plan={plan} users={users} meId={me?.id}
        onEditGoal={onEditGoal} onEvidence={onEvidence} requestDelete={setDelGoal} reload={reload} showToast={showToast} th={th} td={td} totalWeight={totalWeight} />

      {acc.can_edit_plan && plan.status !== 'approved' && (
        <button onClick={() => onEditGoal({ goal_type: 'business' })} style={{ ...btn(), marginTop: 12 }}>+ Add Goal</button>
      )}

      {/* Workflow bar */}
      <div style={{ marginTop: 24, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, color: 'var(--muted2)' }}>
          Status: <StatusPill status={plan.status} />
          {plan.approved_by_name && <span style={{ marginLeft: 8 }}>· Approved by {plan.approved_by_name}</span>}
          {totalWeight !== 100 && <span style={{ marginLeft: 8, color: totalWeight > 100 ? '#DC2626' : '#D97706' }}>· Weights total {totalWeight}% (should be 100%)</span>}
          {plan.status !== 'approved' && allGoals.length > 0 && pendingRows > 0 && (
            <span style={{ marginLeft: 8, color: '#D97706' }}>· {pendingRows} row{pendingRows === 1 ? '' : 's'} pending approval</span>
          )}
        </div>
        <div style={{ flex: 1 }} />
        {acc.is_owner && plan.status === 'draft' && (
          allApproved
            ? <button onClick={submit} style={btn()}>Submit plan for approval</button>
            : <button disabled title="Every goal row must be approved first" style={{ ...btn('#E5E7EB', '#9CA3AF'), cursor: 'not-allowed' }}>Submit plan ({pendingRows} row{pendingRows === 1 ? '' : 's'} pending)</button>
        )}
        {acc.is_manager && plan.status === 'submitted' && (
          allApproved
            ? <button onClick={approve} style={btn('#16A34A')}>Approve whole plan</button>
            : <button disabled title="Approve every row first" style={{ ...btn('#E5E7EB', '#9CA3AF'), cursor: 'not-allowed' }}>Approve plan ({pendingRows} pending)</button>
        )}
        {(acc.is_manager || acc.is_admin) && plan.status === 'approved' && <button onClick={() => { setPlanReason(''); setPlanReopen(true); }} style={btn('var(--surface2)', '#B45309')}>Reopen plan (new version)</button>}
      </div>

      {plan.status === 'approved' && (
        <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: 10, color: '#16A34A', fontSize: 13, fontWeight: 700 }}>
          ✓ Fully approved{plan.approved_by_name ? ` by ${plan.approved_by_name}` : ''}{plan.approved_at ? ` · ${fmtDMY(plan.approved_at)}` : ''}
        </div>
      )}

      {planReopen && (
        <Modal title="Reopen plan" onClose={() => setPlanReopen(false)}>
          <p style={{ fontSize: 13, color: 'var(--muted2)', marginTop: 0 }}>This returns the fully-approved plan to draft as a new version. Please give a reason.</p>
          <label style={lbl}>Reason for reopening</label>
          <textarea value={planReason} onChange={e => setPlanReason(e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} placeholder="Explain why the plan is being reopened…" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setPlanReopen(false)} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
            <button onClick={() => { if (!planReason.trim()) { showToast('Please write a reason.'); return; } setPlanReopen(false); reopenPlan(planReason); }} style={btn('#B45309')}>Reopen Plan</button>
          </div>
        </Modal>
      )}

      {/* Version history */}
      {plan.versions?.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', marginBottom: 10 }}>Version history</div>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
            {plan.versions.map(v => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
                <span style={{ fontWeight: 700 }}>v{v.version}</span>
                <StatusPill status={v.status} />
                <span style={{ color: 'var(--muted2)' }}>by {v.created_by_name}</span>
                <div style={{ flex: 1 }} />
                <span style={{ color: 'var(--muted)' }}>{fmtDMY(v.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {delGoal && (
        <ConfirmModal title="Delete goal" danger confirmLabel="Delete"
          message={`Delete the goal "${delGoal.title || 'Untitled'}"? This cannot be undone.`}
          onConfirm={confirmDelete} onClose={() => setDelGoal(null)} />
      )}
      {delPlan && (
        <ConfirmModal title="Delete plan" danger confirmLabel="Delete plan"
          message={`Delete the draft “${plan.name || ''}” for ${plan.year}, including all its goals and evidence? This cannot be undone.`}
          onConfirm={confirmDeletePlan} onClose={() => setDelPlan(false)} />
      )}
      {renaming && (
        <Modal title="Rename draft" onClose={() => setRenaming(false)} width={420}>
          <label style={lbl}>Plan name</label>
          <input style={inp} value={renameVal} autoFocus onChange={e => setRenameVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') confirmRename(); }} placeholder="e.g. Draft 1" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button onClick={() => setRenaming(false)} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
            <button onClick={confirmRename} style={btn()}>Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
function MilestonesCell({ g }) {
  return (
    <div>
      {g.target_milestones && String(g.target_milestones).trim()
        ? <div style={{ whiteSpace: 'pre-wrap' }}>{g.target_milestones}</div>
        : <span style={{ color: 'var(--muted)' }}>—</span>}
      {g.notes && String(g.notes).trim() ? (
        <div style={{ marginTop: 6, fontStyle: 'italic', color: 'var(--muted2)', fontSize: 11.5 }}>Note: {g.notes}</div>
      ) : null}
    </div>
  );
}

// Mid/End review display: rating (bold) + note.
function ReviewCell({ rating, note }) {
  if (!rating && !note) return <span style={{ color: 'var(--muted)' }}>—</span>;
  return (
    <div>
      {rating ? <div style={{ fontWeight: 700, color: 'var(--text)' }}>{rating}</div> : null}
      {note ? <div style={{ whiteSpace: 'pre-wrap', marginTop: rating ? 2 : 0 }}>{note}</div> : null}
    </div>
  );
}

function GoalsTable({ title, goals, plan, users, meId, onEditGoal, onEvidence, requestDelete, reload, showToast, th, td, totalWeight }) {
  const acc = plan.my_access || {};
  const [reopenGoal, setReopenGoal] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [commentGoal, setCommentGoal] = useState(null);
  const isMgr = acc.is_manager || acc.is_admin;
  const approveRow = async (g) => {
    try { await ippApi.put(`/goals/${g.id}/approval`, {}); reload(); } catch (e) { showToast(e.error || 'Failed'); }
  };
  const doReopen = async () => {
    if (!reopenReason.trim()) { showToast('Please write a reason.'); return; }
    try { await ippApi.post(`/goals/${reopenGoal.id}/reopen`, { reason: reopenReason }); setReopenGoal(null); setReopenReason(''); reload(); }
    catch (e) { showToast(e.error || 'Failed'); }
  };
  const rowApproved = (g) => (g.row_status || (g.approved ? 'approved' : 'submitted_draft')) === 'approved';
  // employee can't edit an approved row; managers always can; shared users can while not approved
  const canEditRow = (g) => acc.is_admin || acc.is_manager || (acc.is_owner && !rowApproved(g)) || (!rowApproved(g) && g.shared_with?.some(s => s.id === meId));

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>{title}</span>
        <span style={{ fontSize: 12, color: totalWeight === 100 ? '#16A34A' : 'var(--muted2)', fontWeight: 600 }}>Total weight: {totalWeight}%</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1280 }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 250 }}>Goal · Target</th>
              <th style={{ ...th, width: 50 }}>Wt %</th>
              <th style={{ ...th, width: 300 }}>Target & Milestones</th>
              <th style={{ ...th, width: 150 }}>Shared with</th>
              <th style={{ ...th, width: 140 }}>Mid-Year</th>
              <th style={{ ...th, width: 140 }}>End-Year</th>
              <th style={{ ...th, width: 80 }}>Evidence</th>
              <th style={{ ...th, width: 95 }}>Approval</th>
              <th style={{ ...th, width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {goals.length === 0 && <tr><td style={{ ...td, textAlign: 'center', color: 'var(--muted)' }} colSpan={9}>No goals yet.</td></tr>}
            {goals.map(g => (
              <tr key={g.id} style={{ background: rowApproved(g) ? 'rgba(22,163,74,0.04)' : 'transparent' }}>
                <td style={{ ...td, fontWeight: 600 }}>{g.title || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td style={td}>{Number(g.weight) || 0}%</td>
                <td style={td}><MilestonesCell g={g} /></td>
                <td style={td}>{(() => {
                  const recipients = (g.shared_with || []).map(s => s.full_name);
                  if (g.is_owner_goal === false) {
                    // Shared-in goal: creator is g.shared_by; include me + other recipients.
                    const names = [g.shared_by, ...recipients].filter(Boolean);
                    return <span style={{ fontSize: 12 }}>Shared with <b>{[...new Set(names)].join(', ')}</b></span>;
                  }
                  if (recipients.length) {
                    const names = [plan.employee?.full_name, ...recipients].filter(Boolean);
                    return <span style={{ fontSize: 12 }}>Shared with <b>{[...new Set(names)].join(', ')}</b></span>;
                  }
                  return '—';
                })()}</td>
                <td style={td}><ReviewCell rating={g.mid_year_rating} note={g.mid_year_review} /></td>
                <td style={td}><ReviewCell rating={g.end_year_rating} note={g.end_year_review} /></td>
                <td style={td}>
                  <button onClick={() => onEvidence(g)} title="Open review evidence (attachments & notes)"
                    style={{ ...btn(g.evidence?.length ? 'rgba(124,58,237,0.10)' : 'var(--surface2)', g.evidence?.length ? ACCENT : 'var(--text)'), padding: '5px 9px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    📎 {g.evidence?.length ? `${g.evidence.length} file${g.evidence.length > 1 ? 's' : ''}` : 'Add'}
                  </button>
                </td>
                <td style={td}>
                  {rowApproved(g) ? (
                    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#16A34A' }}>✓ Approved</span>
                      <span style={{ fontSize: 9.5, color: 'var(--muted)' }}>{g.approved_by_name}</span>
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#D97706' }}>Submitted Draft</span>
                      {g.reopen_reason && <span style={{ fontSize: 9.5, color: '#B45309' }} title={g.reopen_reason}>Reopened</span>}
                    </span>
                  )}
                  {isMgr && (
                    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {rowApproved(g)
                        ? <button onClick={() => { setReopenGoal(g); setReopenReason(''); }} style={{ ...btn('var(--surface2)', '#B45309'), padding: '3px 8px', fontSize: 10.5 }}>Reopen</button>
                        : <button onClick={() => approveRow(g)} style={{ ...btn('#16A34A', '#fff'), padding: '3px 8px', fontSize: 10.5 }}>Approve</button>}
                      <button onClick={() => setCommentGoal(g)} style={{ ...btn('var(--surface2)', ACCENT), padding: '3px 8px', fontSize: 10.5 }}>💬 {g.comments?.length || 0}</button>
                    </div>
                  )}
                  {!isMgr && (g.comments?.length > 0) && (
                    <div style={{ marginTop: 4 }}>
                      <button onClick={() => setCommentGoal(g)} style={{ ...btn('var(--surface2)', ACCENT), padding: '3px 8px', fontSize: 10.5 }}>💬 {g.comments.length}</button>
                    </div>
                  )}
                </td>
                <td style={td}>
                  {canEditRow(g) && <button onClick={() => onEditGoal(g)} style={{ ...btn('var(--surface2)', 'var(--text)'), padding: '4px 8px', fontSize: 11, marginBottom: 4 }}>Edit</button>}
                  {(acc.is_manager || acc.is_admin || (acc.is_owner && !rowApproved(g))) && <button onClick={() => requestDelete(g)} style={{ ...btn('#FEF2F2', '#DC2626'), padding: '4px 8px', fontSize: 11 }}>Del</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reopenGoal && (
        <Modal title="Reopen goal" onClose={() => setReopenGoal(null)}>
          <p style={{ fontSize: 13, color: 'var(--muted2)', marginTop: 0 }}>This returns the goal to <b>Submitted Draft</b> for further changes. Everyone shared on it will be notified with your reason.</p>
          <label style={lbl}>Reason for reopening</label>
          <textarea value={reopenReason} onChange={e => setReopenReason(e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} placeholder="Explain why this goal is being reopened…" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button onClick={() => setReopenGoal(null)} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
            <button onClick={doReopen} style={btn('#B45309')}>Reopen Goal</button>
          </div>
        </Modal>
      )}

      {commentGoal && (
        <GoalComments goal={commentGoal} canComment={isMgr} onClose={() => setCommentGoal(null)} reload={reload} showToast={showToast} />
      )}
    </div>
  );
}

// Per-goal comments thread (manager posts; everyone shared is notified).
function GoalComments({ goal, canComment, onClose, reload, showToast }) {
  const [list, setList] = useState(goal.comments || []);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => ippApi.get(`/goals/${goal.id}/comments`).then(setList).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [goal.id]);
  const post = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try { await ippApi.post(`/goals/${goal.id}/comments`, { body }); setBody(''); load(); reload(); }
    catch (e) { showToast(e.error || 'Failed'); }
    setBusy(false);
  };
  return (
    <Modal title={`Comments — ${goal.title || 'Goal'}`} onClose={onClose}>
      <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        {list.length === 0 && <div style={{ fontSize: 13, color: 'var(--muted)' }}>No comments yet.</div>}
        {list.map(c => (
          <div key={c.id} style={{ background: 'var(--surface2)', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{c.body}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{c.author_name} · {fmtDMY(c.created_at)}</div>
          </div>
        ))}
      </div>
      {canComment ? (
        <>
          <label style={lbl}>Add a comment <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--muted)' }}>(everyone shared on this goal is notified)</span></label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} placeholder="Write a comment…" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
            <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Close</button>
            <button onClick={post} disabled={busy} style={btn()}>Post Comment</button>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Close</button></div>
      )}
    </Modal>
  );
}

// ── Profile (document header) editor ────────────────────────────────────────
function ProfileModal({ users, onClose, onSaved, showToast }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { ippApi.get('/profile').then(setF).catch(() => setF({})); }, []);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await ippApi.put('/profile', f); showToast('Profile saved'); onSaved && onSaved(); onClose(); }
    catch (e) { showToast(e.error || 'Failed'); }
    setSaving(false);
  };
  if (!f) return <Modal title="My Profile" onClose={onClose}><div style={{ color: 'var(--muted)' }}>Loading…</div></Modal>;
  const field = (k, label, type = 'text') => (
    <div><label style={lbl}>{label}</label><input type={type} style={inp} value={f[k] || ''} onChange={e => set(k, e.target.value)} /></div>
  );
  return (
    <Modal title="My Profile — Plan Header" onClose={onClose} width={640}>
      <p style={{ fontSize: 12, color: 'var(--muted2)', marginTop: 0, marginBottom: 16 }}>This information fills the header of your Individual Performance Plan document. It is general — it applies to every year and you can edit it anytime.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {field('staff_no', 'Staff No')}
        {field('position_title', 'Position Title')}
        {field('function_name', 'Function Name')}
        {field('salary_grade', 'Salary Grade')}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl}>Line Manager (Name)</label>
          <select style={inp} value={f.line_manager_name || ''} onChange={e => set('line_manager_name', e.target.value)}>
            <option value="">— Select line manager —</option>
            {users.map(u => <option key={u.id} value={u.full_name}>{u.full_name}</option>)}
          </select>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Choose from people who have Performance Plans access.</div>
        </div>
        {field('join_date', 'Join Date', 'date')}
        {field('date_to_position', 'Date to Position', 'date')}
        {field('date_to_sct', 'Date to SCT', 'date')}
        {field('date_to_salary_group', 'Date to Salary Group', 'date')}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
        <button onClick={save} disabled={saving} style={btn()}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

// ── Goal row editor ─────────────────────────────────────────────────────────
function GoalModal({ plan, users, goal, onClose, onSaved, showToast }) {
  const MS_TEMPLATE = 'Outstanding: \nAbove Target: \nTarget: \nBelow Target: \nUnsatisfactory: ';
  const [f, setF] = useState(goal || { goal_type: 'business', title: '', weight: 0, target_milestones: MS_TEMPLATE, notes: '', mid_year_rating: '', mid_year_review: '', end_year_rating: '', end_year_review: '' });
  const [sharedIds, setSharedIds] = useState(goal?.shared_with?.map(s => s.id) || []);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, weight: Number(f.weight) || 0, shared_with_ids: sharedIds };
      if (goal?.id) await ippApi.put(`/goals/${goal.id}`, payload);
      else await ippApi.post(`/plans/${plan.id}/goals`, payload);
      onSaved();
    } catch (e) { showToast(e.error || 'Failed'); }
    setSaving(false);
  };
  const ta = { ...inp, minHeight: 70, resize: 'vertical', lineHeight: 1.5 };
  const bigta = { ...inp, minHeight: 150, resize: 'vertical', lineHeight: 1.6 };

  return (
    <Modal title={goal?.id ? 'Edit Goal' : 'New Goal'} onClose={onClose} width={780}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '66vh', overflowY: 'auto', paddingRight: 4 }}>
        <div style={{ display: 'flex', gap: 14 }}>
          <div style={{ flex: 1 }}><label style={lbl}>Goal type</label>
            <select style={inp} value={f.goal_type} onChange={e => set('goal_type', e.target.value)}>
              <option value="business">Business / Individual Goal</option>
              <option value="improvement">Continuous Improvement Goal</option>
            </select>
          </div>
          <div style={{ width: 120 }}><label style={lbl}>Weight %</label>
            <input type="number" min="0" max="100" style={inp} value={f.weight} onChange={e => set('weight', e.target.value)} />
          </div>
        </div>
        <div><label style={lbl}>Business / Individual Goal</label><textarea style={ta} value={f.title || ''} onChange={e => set('title', e.target.value)} placeholder="Describe the goal / objective" /></div>

        {/* Target & Milestones — pre-filled with the five rating rows, fully editable */}
        <div>
          <label style={lbl}>Target &amp; Milestones</label>
          <textarea style={bigta} value={f.target_milestones || ''} onChange={e => set('target_milestones', e.target.value)}
            placeholder={'Outstanding: \nAbove Target: \nTarget: \nBelow Target: \nUnsatisfactory: '} />
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>The five rating rows are pre-filled — type each criterion after the label. You can add A. and B. sections too. Appears centered in the document.</div>
        </div>

        <div><label style={lbl}>Notes (optional — shown under Target &amp; Milestones)</label>
          <textarea style={ta} value={f.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="General notes for this goal…" /></div>

        {/* Mid-Year & End-Year reviews: a rating + a free note */}
        <div style={{ display: 'flex', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Mid-Year Review</label>
            <select style={{ ...inp, marginBottom: 8 }} value={f.mid_year_rating || ''} onChange={e => set('mid_year_rating', e.target.value)}>
              <option value="">— Rating —</option>
              {RANKINGS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <textarea style={ta} value={f.mid_year_review || ''} onChange={e => set('mid_year_review', e.target.value)} placeholder="Mid-year note…" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>End-Year Review</label>
            <select style={{ ...inp, marginBottom: 8 }} value={f.end_year_rating || ''} onChange={e => set('end_year_rating', e.target.value)}>
              <option value="">— Rating —</option>
              {RANKINGS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <textarea style={ta} value={f.end_year_review || ''} onChange={e => set('end_year_review', e.target.value)} placeholder="End-year note…" />
          </div>
        </div>

        <div><label style={lbl}>Shared with</label>
          <MultiUserSelect users={users.filter(u => u.id !== plan.user_id)} value={sharedIds} onChange={setSharedIds} placeholder="Share this goal with people…" />
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
            Shared with <b>{[plan.employee?.full_name, ...users.filter(u => sharedIds.includes(u.id)).map(u => u.full_name)].filter(Boolean).join(', ')}</b>.
            {' '}Everyone shares the same live goal — edits sync both ways. Either side can remove a share.
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
        <button onClick={save} disabled={saving} style={btn()}>{saving ? 'Saving…' : 'Save Goal'}</button>
      </div>
    </Modal>
  );
}

// ── Evidence: real file upload + optional link/note ─────────────────────────
function EvidenceModal({ goal, onClose, onSaved, showToast, canEdit }) {
  const [items, setItems] = useState(goal.evidence || []);
  const [note, setNote] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const token = localStorage.getItem('flowup_token');

  const addLink = async () => {
    if (!note.trim() && !fileUrl.trim()) return;
    try {
      const ev = await ippApi.post(`/goals/${goal.id}/evidence`, { note, file_url: fileUrl, file_name: fileUrl ? fileUrl.split('/').pop() : '' });
      setItems(p => [...p, ev]); setNote(''); setFileUrl(''); onSaved();
    } catch (e) { showToast(e.error || 'Failed'); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file); if (note.trim()) fd.append('note', note);
      const res = await fetch(`/api/ipp/goals/${goal.id}/evidence/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || 'Upload failed'); }
      const ev = await res.json();
      setItems(p => [...p, ev]); setNote(''); onSaved();
    } catch (err) { showToast(err.message || 'Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const del = async (id) => { try { await ippApi.delete(`/evidence/${id}`); setItems(p => p.filter(x => x.id !== id)); onSaved(); } catch (e) { showToast(e.error || 'Failed'); } };

  return (
    <Modal title="Add Evidence Files" onClose={onClose} width={580}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
        {items.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>No evidence yet.</div>}
        {items.map(ev => (
          <div key={ev.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, fontSize: 12.5 }}>
            {ev.note && <div style={{ marginBottom: 4 }}>{ev.note}</div>}
            {ev.stored_path
              ? <a href={`/api/ipp/evidence/${ev.id}/download?token=${encodeURIComponent(token || '')}`} target="_blank" rel="noreferrer" style={{ color: ACCENT, fontWeight: 600 }}>📎 {ev.file_name || 'Download file'}{ev.size_bytes ? ` (${Math.round(ev.size_bytes / 1024)} KB)` : ''}</a>
              : ev.file_url
                ? <a href={ev.file_url} target="_blank" rel="noreferrer" style={{ color: ACCENT, fontWeight: 600 }}>🔗 {ev.file_name || ev.file_url}</a>
                : ev.file_name && <span style={{ color: 'var(--muted2)' }}>📎 {ev.file_name}</span>}
            <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
              <span>{ev.uploaded_by_name}</span>
              {canEdit && <span onClick={() => del(ev.id)} style={{ color: '#DC2626', cursor: 'pointer', fontWeight: 600 }}>Remove</span>}
            </div>
          </div>
        ))}
      </div>
      {canEdit && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input style={inp} value={note} onChange={e => setNote(e.target.value)} placeholder="Description (optional)" />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input ref={fileRef} type="file" onChange={onFile} disabled={uploading} style={{ fontSize: 12, flex: 1 }} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} style={btn()}>{uploading ? 'Uploading…' : '📎 Upload file'}</button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={inp} value={fileUrl} onChange={e => setFileUrl(e.target.value)} placeholder="…or paste a link / URL" />
            <button onClick={addLink} style={btn('var(--surface2)', 'var(--text)')}>+ Add link</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
